import { Input } from '../../ui/Input/Input';
import TextArea from '../../ui/TextArea/TextArea';
import { H2 } from '../../ui/H2/H2';
import { QuestionLayout } from '../Manual/QuestionLayout/QuestionLayout';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  setPreStepInputError,
  setDisableNextStep
} from '../../../store/slices/stepperSlice';
import { useAppSelector } from '../../../hooks/redux';
import { setStartAiSystemBodyRequest } from '../../../store/slices/stepperSlice';
import Loader from '../../ui/Loader/Loader';
import { ErrorMessage } from '../../ui/ErrorMessage/ErrorMessage';
import { QuestionMarkCircle } from '../../../icons/SvgIcons';
import { RadioButtonCard } from '../../ui/RadioButton/RadioButtonCard';
import { radioButtonCardOptions } from './PreStep.consts';
import { AssessmentType } from '../../../types/AISystems.types';
import { StartAiSystemRequest } from '../../../types/AISystems.types';
import { useLocation } from 'react-router-dom';

export function PreStep() {
  const isConversationalAIEnabled = false; // hide switch to AI-powered assessment button until feature is ready for release

  const dispatch = useDispatch();
  const { aiSystemId } = useAppSelector((state) => state.stepper);
  const location = useLocation();
  const basicInformation = location.state?.basicInformation as
    | StartAiSystemRequest
    | undefined;
  const {
    selectedEntity,
    creatingAiSystem,
    preStepInputError,
    preStepPart,
    startAiSystemBodyResponse
  } = useAppSelector((state) => state.stepper);
  const [aiSystemName, setAiSystemName] = useState(
    basicInformation?.name ?? ''
  );
  const [aiSystemVersion, setAiSystemVersion] = useState(
    basicInformation?.version ?? ''
  );
  const [description, setDescription] = useState(
    basicInformation?.description ?? ''
  );
  const [organizationName, setOrganizationName] = useState(
    basicInformation?.organization ?? ''
  );
  const [compliance, setCompliance] = useState(
    basicInformation?.complianceContact ?? ''
  );
  const [entity, setEntity] = useState('');
  const [selectedMethod, setSelectedMethod] = useState<AssessmentType | null>(
    isConversationalAIEnabled ? null : 'QUESTIONNAIRE'
  );

  const [hovered, setHovered] = useState(false);

  useEffect(() => {
    const isEmpty =
      preStepPart === 'basicInformation'
        ? aiSystemName.trim() === '' ||
          aiSystemVersion.trim() === '' ||
          description.trim() === '' ||
          organizationName.trim() === '' ||
          compliance.trim() === '' ||
          !selectedMethod
        : !selectedEntity;

    dispatch(setDisableNextStep(isEmpty));
  }, [
    aiSystemName,
    aiSystemVersion,
    organizationName,
    description,
    compliance,
    selectedEntity,
    preStepPart,
    selectedMethod,
    dispatch
  ]);

  useEffect(() => {
    if (preStepPart !== 'basicInformation') return;

    dispatch(
      setStartAiSystemBodyRequest({
        name: aiSystemName,
        version: aiSystemVersion,
        description: description,
        organization: organizationName,
        complianceContact: compliance,
        assessmentType: selectedMethod,
        ...(aiSystemId && { aiSystemId })
      })
    );
  }, [
    aiSystemName,
    aiSystemVersion,
    organizationName,
    description,
    compliance,
    preStepPart,
    selectedMethod,
    dispatch
  ]);

  useEffect(() => {
    if (!selectedEntity) return;

    setEntity(selectedEntity);
    dispatch(
      setPreStepInputError({
        ...preStepInputError,
        entity: undefined
      })
    );
  }, [selectedEntity]);

  const entityQuestion =
    startAiSystemBodyResponse?.question?.id === 'E1' &&
    startAiSystemBodyResponse.question;

  const entityQeustionDefinitions = entityQuestion
    ? entityQuestion?.options
        ?.map((option) => option.description)
        .filter((description): description is string => !!description)
    : [];

  return (
    <div className="flex flex-col gap-5 px-12 mt-5 mb-6">
      {creatingAiSystem ? (
        <div className="absolute top-1/2 left-1/2">
          <Loader />
        </div>
      ) : (
        <>
          {preStepPart === 'basicInformation' ? (
            <>
              <H2 title="Basic Information" textSize="text-base" />
              <div>
                <Input
                  id="ai-system-name"
                  label="AI System Name *"
                  maxLength={254}
                  onChange={(e) => {
                    setAiSystemName(e.target.value);
                    dispatch(
                      setPreStepInputError({
                        ...preStepInputError,
                        name: undefined
                      })
                    );
                  }}
                  value={aiSystemName}
                  placeholder="Enter the AI system name"
                  labelClassName="label"
                  inputClassName={`input ${
                    preStepInputError?.name
                      ? 'border-rose-400 text-rose-800 hover:border-rose-400 focus:ring-rose-400'
                      : ''
                  }`}
                />
                {preStepInputError?.name && (
                  <ErrorMessage message={preStepInputError.name} />
                )}
              </div>
              <div>
                <Input
                  id="ai-system-version"
                  label="AI System Version *"
                  maxLength={254}
                  onChange={(e) => {
                    setAiSystemVersion(e.target.value);
                    dispatch(
                      setPreStepInputError({
                        ...preStepInputError,
                        version: undefined
                      })
                    );
                  }}
                  value={aiSystemVersion}
                  placeholder="eg. v1.0.0"
                  labelClassName="label"
                  inputClassName={`input ${
                    preStepInputError?.version
                      ? 'border-rose-400 text-rose-800 hover:border-rose-400 focus:ring-rose-400'
                      : ''
                  }`}
                />
                {preStepInputError?.version && (
                  <ErrorMessage message={preStepInputError.version} />
                )}
              </div>
              <div>
                <TextArea
                  label="Description *"
                  hint="Please provide a detailed description of your system’s intended purpose and context of use , as this information is the primary data source for risk classification and the generation of legal documentation. Specify if the system operates in critical areas like biometrics, education, employment, or infrastructure , or if it generates synthetic content. A comprehensive description is essential for an accurate analysis of your risk level and for defining your specific obligations."
                  onChange={(e) => {
                    setDescription(e);
                    dispatch(
                      setPreStepInputError({
                        ...preStepInputError,
                        description: undefined
                      })
                    );
                  }}
                  value={description}
                  placeholder="e.g. We built a system that automatically screens job applicants by analyzing their CVs and ranking them. It is commercially deployed and sold to employers across the EU..."
                  error={preStepInputError?.description}
                />
              </div>
              <div>
                <Input
                  id="ogranization-name"
                  label="Organization Name *"
                  maxLength={254}
                  onChange={(e) => {
                    setOrganizationName(e.target.value);
                    dispatch(
                      setPreStepInputError({
                        ...preStepInputError,
                        organization: undefined
                      })
                    );
                  }}
                  value={organizationName}
                  placeholder="Enter the organization name"
                  labelClassName="label"
                  inputClassName={`input ${
                    preStepInputError?.organization
                      ? 'border-rose-400 text-rose-800 hover:border-rose-400 focus:ring-rose-400'
                      : ''
                  }`}
                />
                {preStepInputError?.organization && (
                  <ErrorMessage message={preStepInputError.organization} />
                )}
              </div>
              <div>
                <Input
                  id="compliance-contact-person"
                  label="Compliance Contact Person *"
                  maxLength={254}
                  onChange={(e) => {
                    setCompliance(e.target.value);
                    dispatch(
                      setPreStepInputError({
                        ...preStepInputError,
                        compliance: undefined
                      })
                    );
                  }}
                  value={compliance}
                  placeholder="Enter your details"
                  labelClassName="label"
                  inputClassName={`input ${
                    preStepInputError?.compliance
                      ? 'border-rose-400 text-rose-800 hover:border-rose-400 focus:ring-rose-400'
                      : ''
                  }`}
                />
                {preStepInputError?.compliance && (
                  <ErrorMessage message={preStepInputError.compliance} />
                )}
              </div>
              {/* Assessment Method */}
              {isConversationalAIEnabled && (
                <div className="flex flex-col gap-6">
                  <div className="flex gap-1">
                    <H2 title="Assessment Method" textSize="text-base" />
                    <div
                      title="You can switch methods later if needed."
                      onMouseEnter={() => setHovered(true)}
                      onMouseLeave={() => setHovered(false)}
                    >
                      {QuestionMarkCircle(hovered)}
                    </div>
                  </div>
                  <div className="flex gap-6">
                    {radioButtonCardOptions.map((option) => (
                      <RadioButtonCard
                        key={option.label}
                        label={option.label}
                        desctiption={option.description}
                        selected={selectedMethod === option.key}
                        onChange={() => {
                          setSelectedMethod(option.key);
                        }}
                        classNames="w-full max-h-[142px]"
                      />
                    ))}
                  </div>
                  <p className="text-xs leading-5 text-gray-900">
                    Results are based on the information provided and current EU
                    AI Act documentation. This assessment does not constitute
                    legal advice.
                  </p>
                </div>
              )}
            </>
          ) : (
            <>
              {entityQuestion && (
                <QuestionLayout
                  question={{
                    ...entityQuestion,
                    definitions: entityQeustionDefinitions
                  }}
                  entity={entity}
                />
              )}
              {preStepInputError?.entity && (
                <ErrorMessage message={preStepInputError.entity} />
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}
